import type { ReactNode } from "react";
import { motion } from "motion/react";
import { formatMoney, PROJECT_COLORS, toMajor, toMinor, type Currency, type ProjectColor } from "@otrack/domain";
import { EditableText, Icon, Tooltip, type IconName, type SelectOption } from "@otrack/ui";
import { formatDuration, HOUR, startOfDay } from "./time";

/** The entrance every page section shares: a short rise, staggered by `index`. */
export function rise(index = 0) {
  return {
    initial: { opacity: 0, y: 10 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.5, ease: [0.16, 1, 0.3, 1] as const, delay: Math.min(index, 8) * 0.045 }
  };
}

export function PageHeader({ title, subtitle, back, actions, eyebrow }: { title: ReactNode; subtitle?: ReactNode; back?: { label: string; href: string }; actions?: ReactNode; eyebrow?: ReactNode }) {
  return <header className="ot-page__header">
    <div className="ot-page__titles">
      {back && <a className="ot-page__back" href={back.href}><Icon name="arrow-left" weight="bold"/>{back.label}</a>}
      {eyebrow && <div className="ot-page__eyebrow">{eyebrow}</div>}
      <h1>{title}</h1>
      {subtitle && <div className="ot-page__subtitle">{subtitle}</div>}
    </div>
    {actions && <div className="ot-page__actions">{actions}</div>}
  </header>;
}

/** A paper surface with a titled head: the building block of every non-Today page. */
export function Panel({ title, icon, description, aside, children, className = "", id, index = 0 }: { title: ReactNode; icon?: IconName; description?: ReactNode; aside?: ReactNode; children: ReactNode; className?: string; id?: string; index?: number }) {
  return <motion.section className={`ot-panel ${className}`.trim()} id={id} aria-label={typeof title === "string" ? title : undefined} {...rise(index)}>
    <div className="ot-panel__head">
      {icon && <span className="ot-panel__icon"><Icon name={icon}/></span>}
      <div className="ot-panel__titles">
        <h2>{title}</h2>
        {description && <p>{description}</p>}
      </div>
      {aside && <div className="ot-panel__aside">{aside}</div>}
    </div>
    {children}
  </motion.section>;
}

/** One setting: what it is on the left, the control on the right. Stacks on narrow screens. */
export function SettingRow({ label, description, children, wide = false, htmlFor }: { label: ReactNode; description?: ReactNode; children: ReactNode; wide?: boolean; htmlFor?: string }) {
  return <div className={`ot-row ${wide ? "ot-row--wide" : ""}`}>
    <div className="ot-row__text">
      {htmlFor ? <label className="ot-row__label" htmlFor={htmlFor}>{label}</label> : <span className="ot-row__label">{label}</span>}
      {description && <span className="ot-row__description">{description}</span>}
    </div>
    <div className="ot-row__control">{children}</div>
  </div>;
}

export function Stat({ label, value, note, accent = false }: { label: string; value: ReactNode; note?: ReactNode; accent?: boolean }) {
  return <div className={`ot-stat ${accent ? "is-accent" : ""}`}>
    <span className="ot-stat__label">{label}</span>
    <strong className="ot-stat__value">{value}</strong>
    {note && <span className="ot-stat__note">{note}</span>}
  </div>;
}

export function Chip({ children, icon, href, tone = "default" }: { children: ReactNode; icon?: IconName; href?: string; tone?: "default" | "live" | "muted" | "warn" }) {
  const body = <>{tone === "live" ? <span className="ot-chip__live" aria-hidden="true"/> : icon ? <Icon name={icon}/> : null}{children}</>;
  return href ? <a className={`ot-pill ot-pill--${tone}`} href={href}>{body}</a> : <span className={`ot-pill ot-pill--${tone}`}>{body}</span>;
}

/** A Client's Picture on a paper-white tile, or its initials on a soft tile until it has one. */
export function Monogram({ name, picture = null, size = 36 }: { name: string; picture?: string | null; size?: number }) {
  if (picture) return <span className="ot-monogram has-picture" style={{ width: size, height: size }} aria-hidden="true"><img src={picture} alt=""/></span>;
  const initials = name.split(/\s+/).filter(Boolean).slice(0, 2).map((word) => word[0]!.toUpperCase()).join("") || "?";
  return <span className="ot-monogram" style={{ width: size, height: size, fontSize: size * 0.38 }} aria-hidden="true">{initials}</span>;
}

/** A centered empty state with a glyph, a line of guidance, and optional actions. */
export function EmptyState({ icon, title, children, actions }: { icon: IconName; title: string; children?: ReactNode; actions?: ReactNode }) {
  return <motion.div className="ot-empty-state" {...rise(1)}>
    <span className="ot-empty-state__icon"><Icon name={icon}/></span>
    <strong>{title}</strong>
    {children && <p>{children}</p>}
    {actions && <div className="ot-empty-state__actions">{actions}</div>}
  </motion.div>;
}

const barDay = new Intl.DateTimeFormat("en-US", { weekday: "short", month: "short", day: "numeric" });

/**
 * Hours per day as small columns, newest on the right; today's column is Ember. Used for a
 * Project's recent activity, in the same voice as Today's month chart.
 */
export function DayBars({ days, end, compact = false, label }: { days: number[]; end: number; compact?: boolean; label: string }) {
  const scale = Math.max(compact ? 6 * HOUR : 8 * HOUR, ...days);
  const first = startOfDay(end, -(days.length - 1));
  return <div className={`ot-bars ${compact ? "ot-bars--compact" : ""}`} role="img" aria-label={label} style={{ ["--ot-bars" as string]: days.length }}>
    {!compact && <i className="ot-bars__ref" style={{ bottom: `${8 * HOUR / scale * 100}%` }}><span className="ot-mono">8h</span></i>}
    {days.map((value, index) => {
      const day = startOfDay(first, index);
      const bar = <span key={index} className={`ot-bars__col ${index === days.length - 1 ? "is-today" : ""}`} style={{ ["--i" as string]: index }}>
        {value > 0 ? <i style={{ height: `${Math.max(compact ? 8 : 3, value / scale * 100)}%` }}/> : <b/>}
      </span>;
      return compact ? bar : <Tooltip key={index} label={`${barDay.format(day)}\n${value ? formatDuration(value) : "Nothing tracked"}`} delay={100}>{bar}</Tooltip>;
    })}
  </div>;
}

/** Major-unit text for an amount in minor units, for inputs. */
export function moneyInput(amount: number | null, currency: Currency): string {
  return amount === null ? "" : String(toMajor(amount, currency));
}

/** Parses money typed in major units. Empty means null; invalid means undefined. */
export function parseMoneyInput(value: string, currency: Currency): number | null | undefined {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const number = Number(trimmed.replace(/\s/g, "").replace(",", "."));
  if (!Number.isFinite(number) || number < 0) return undefined;
  return toMinor(number, currency);
}

/** The same typed number in another currency's minor units: 28 USD stays "28" as CLP. */
export function convertTyped(amount: number | null, from: Currency, to: Currency): number | null {
  return amount === null ? null : parseMoneyInput(moneyInput(amount, from), to) ?? null;
}

export function money(amount: number, currency: Currency): string {
  return formatMoney({ amount, currency });
}

/**
 * An amount that saves itself, shown with its currency. `onCommit` receives minor units or null
 * when cleared; typing something unreadable keeps the draft open with an error.
 */
export function MoneyField({ amount, currency, onCommit, placeholder, perHour = false, size, id, "aria-label": ariaLabel }: {
  amount: number | null;
  currency: Currency;
  onCommit: (amount: number | null) => void | Promise<void>;
  placeholder?: string;
  perHour?: boolean;
  size?: "md" | "sm";
  id?: string;
  "aria-label"?: string;
}) {
  return <EditableText
    {...(id ? { id } : {})}
    {...(ariaLabel ? { "aria-label": ariaLabel } : {})}
    {...(size ? { size } : {})}
    value={moneyInput(amount, currency)}
    inputMode="decimal"
    placeholder={placeholder ?? "0"}
    trailing={<span className="ot-unit">{currency}{perHour ? <span>/h</span> : null}</span>}
    onCommit={async (text) => {
      const parsed = parseMoneyInput(text, currency);
      if (parsed === undefined) return "Enter a positive amount, like 45 or 45.50.";
      if (parsed !== amount) await onCommit(parsed);
      return null;
    }}
  />;
}

export const COMMON_CURRENCIES = ["USD", "EUR", "CLP", "VES"];

const CURRENCY_NAMES: Record<string, string> = { USD: "US dollar", EUR: "Euro", CLP: "Chilean peso", VES: "Venezuelan bolívar" };

export function currencyOptions(extra: Array<string | null | undefined> = []): Array<SelectOption<string>> {
  return [...new Set([...COMMON_CURRENCIES, ...extra.filter((code): code is string => Boolean(code))])]
    .map((code) => ({ value: code, label: code, hint: CURRENCY_NAMES[code] }));
}

const COLOR_NAMES: Record<ProjectColor, string> = {
  rose: "Rose", plum: "Plum", violet: "Violet", indigo: "Indigo", blue: "Blue", sky: "Sky",
  teal: "Teal", sage: "Sage", moss: "Moss", olive: "Olive", gold: "Gold", slate: "Slate"
};

/** The Project palette as a row of swatches. Arrow keys move the choice. */
export function ColorPicker({ value, onChange, "aria-label": ariaLabel = "Color" }: { value: ProjectColor; onChange: (color: ProjectColor) => void; "aria-label"?: string }) {
  const index = PROJECT_COLORS.indexOf(value);
  return <div className="ot-colors" role="radiogroup" aria-label={ariaLabel} onKeyDown={(event) => {
    const delta = event.key === "ArrowRight" || event.key === "ArrowDown" ? 1 : event.key === "ArrowLeft" || event.key === "ArrowUp" ? -1 : 0;
    if (!delta) return;
    event.preventDefault();
    const next = PROJECT_COLORS[(index + delta + PROJECT_COLORS.length) % PROJECT_COLORS.length]!;
    onChange(next);
    event.currentTarget.querySelector<HTMLElement>(`[data-color="${next}"]`)?.focus();
  }}>
    {PROJECT_COLORS.map((color) => <Tooltip key={color} label={COLOR_NAMES[color]} delay={200}>
      <button
        type="button"
        role="radio"
        aria-checked={color === value}
        aria-label={COLOR_NAMES[color]}
        tabIndex={color === value ? 0 : -1}
        data-color={color}
        className={`ot-colors__swatch ${color === value ? "is-on" : ""}`}
        style={{ ["--pc" as string]: `var(--ot-pc-${color})` }}
        onClick={() => onChange(color)}
      />
    </Tooltip>)}
  </div>;
}
